import React, { useEffect, useRef, useState } from "react";
import InputField from "../InputField";
import SubmitButton from "../SubmitButton";
import { useAuthActions } from "../../features/auth/useAuthActions";
import { useOTP } from "../../features/auth/useOTP";

export default function ForgotPasswordForm({ onBackToLogin }) {
  const { forgotPassword } = useAuthActions();
  const { requestOTP, loading: otpLoading } = useOTP();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  const [otpSent, setOtpSent] = useState(false);
  const [localError, setLocalError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [timer, setTimer] = useState(120);
  const [canResend, setCanResend] = useState(false);

  const intervalRef = useRef(null);

  // شمارش معکوس ارسال مجدد OTP
  useEffect(() => {
    if (otpSent && timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [otpSent, timer]);

  // درخواست ارسال OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();

    setLocalError("");
    setSuccessMessage("");

    if (
      !phoneNumber ||
      phoneNumber.length !== 11 ||
      !phoneNumber.startsWith("09")
    ) {
      setLocalError(
        "لطفاً یک شماره موبایل معتبر ۱۱ رقمی (شروع با ۰۹) وارد کنید."
      );
      return;
    }

    try {
      await requestOTP(phoneNumber, "reset_password");

      setOtpSent(true);
      setTimer(120);
      setCanResend(false);
      setSuccessMessage("کد تایید برای شما ارسال شد.");
    } catch (err) {
      setLocalError(
        err.response?.data?.detail ||
          "خطا در ارسال کد تایید. مجدداً تلاش کنید."
      );
    }
  };

  // ثبت نهایی تغییر رمز عبور
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLocalError("");
    setSuccessMessage("");

    if (
      !phoneNumber ||
      phoneNumber.length !== 11 ||
      !phoneNumber.startsWith("09")
    ) {
      setLocalError("شماره موبایل معتبر وارد کنید.");
      return;
    }

    if (!otpSent) {
      setLocalError("ابتدا کد تایید را دریافت کنید.");
      return;
    }

    if (!otp || otp.length < 4) {
      setLocalError("کد تایید پیامک شده را وارد کنید.");
      return;
    }

    if (newPassword.length < 8) {
      setLocalError("رمز عبور باید حداقل ۸ کاراکتر باشد.");
      return;
    }

    if (newPassword !== passwordConfirm) {
      setLocalError("رمز عبور با تکرار آن مطابقت ندارد.");
      return;
    }

    setLoading(true);

    try {
      await forgotPassword({
        phoneNumber,
        otp,
        newPassword,
        newPasswordConfirm: passwordConfirm,
      });

      setSuccessMessage("رمز عبور با موفقیت تغییر کرد.");

      setTimeout(() => {
        onBackToLogin?.();
      }, 2000);
    } catch (err) {
      setLocalError(
        err.response?.data?.detail ||
          "تغییر رمز عبور ناموفق بود. اطلاعات را بررسی کنید."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* رمز عبور جدید */}
      <InputField
        label="رمز عبور جدید"
        name="newPassword"
        type="password"
        placeholder="حداقل ۸ کاراکتر"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />

      {/* تکرار رمز عبور */}
      <InputField
        label="تکرار رمز عبور"
        name="passwordConfirm"
        type="password"
        placeholder="تکرار رمز عبور جدید"
        value={passwordConfirm}
        onChange={(e) => setPasswordConfirm(e.target.value)}
      />

      {/* شماره موبایل + دکمه ارسال OTP */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">
          شماره موبایل
        </label>

        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="text"
              placeholder="مثال: 09123456789"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
              dir="ltr"
            />
          </div>

          <button
            type="button"
            onClick={handleRequestOTP}
            disabled={(otpSent && !canResend) || otpLoading}
            className={`px-4 py-2 text-sm font-semibold rounded-md border transition whitespace-nowrap ${
              otpSent && !canResend
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
            }`}
          >
            {otpLoading
              ? "در حال ارسال..."
              : otpSent
              ? canResend
                ? "ارسال مجدد"
                : "ارسال شد"
              : "ارسال کد"}
          </button>
        </div>
      </div>

      {/* فیلد OTP */}
      {otpSent && (
        <div className="bg-blue-50/50 p-3 rounded-md border border-blue-100 flex flex-col gap-2">
          <InputField
            label="کد تایید پیامک شده"
            name="otp"
            type="text"
            placeholder="کد ۶ رقمی"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />

          <div className="text-xs text-gray-500 pr-1">
            {!canResend && (
              <span>
                ارسال مجدد کد تا {Math.floor(timer / 60)}:
                {String(timer % 60).padStart(2, "0")} دیگر
              </span>
            )}
          </div>
        </div>
      )}

      {/* پیام خطا */}
      {localError && (
        <p className="text-red-600 text-sm pr-1">{localError}</p>
      )}

      {/* پیام موفقیت */}
      {successMessage && (
        <p className="text-green-600 text-sm pr-1">
          {successMessage}
        </p>
      )}

      {/* دکمه ثبت نهایی */}
      <SubmitButton
        text={loading ? "در حال تغییر رمز..." : "تغییر رمز عبور"}
      />
    </form>
  );
}
